import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, ViewEncapsulation,
} from '@angular/core';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {NavigationEnd, Router} from '@angular/router';
import {Observable, Subject, combineLatest} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';
import {ThemeService} from '@app/services/theme.service';
import {NavigationMenuService} from '@app/services/navigation-menu.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {ProjectContextService} from '@app/services/project-context.service';
import {NavLayoutState} from '../model/nav-layout';
import {ResolvedBookmark, ResolvedNavNode} from '../model/nav-tree';

const OPEN_DELAY = 150;
const CLOSE_DELAY = 300;
const FAVORITES_FLYOUT = '__favorites';

interface Flyout {
  id: string;
  label: string;
  top: number;
  pinned: boolean;
}

interface Tooltip {
  text: string;
  top: number;
}

/**
 * The side menu. Expanded: search, favorites, the project's own pages under its name, then
 * everything else, with groups that open in place and remember it. As a rail: icons only,
 * a tooltip for each, and groups that open as a flyout beside it. On a phone it is a drawer
 * that traps focus until closed. No page closes it: editors lay themselves out beside it.
 */
@Component({
  selector: 'app-nav-sidebar',
  templateUrl: './nav-sidebar.component.html',
  styleUrls: ['./nav-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NavSidebarComponent implements OnDestroy {
  readonly FAVORITES_FLYOUT = FAVORITES_FLYOUT;
  readonly layout$: Observable<NavLayoutState> = this.navLayout.state$;
  readonly available$ = this.menu.available$;
  readonly favorites$ = this.menu.favorites$;
  readonly hiddenFavoriteCount$ = this.menu.hiddenFavoriteCount$;
  readonly activeIds$ = this.menu.activeIds$;
  readonly starredKeys$ = this.menu.starredKeys$;
  readonly expandedGroups$ = this.navigationState.preferences$.pipe(map((preferences) => new Set(preferences.expandedGroups)));
  readonly context$ = this.projectContext.context$;
  readonly isDarkTheme$ = this.themeService.isDarkTheme$;
  readonly isInfluencer$ = this.menu.isInfluencer$;
  /** The project's own pages, shown under its name while one is open. */
  readonly projectNodes$ = this.menu.main$.pipe(map((nodes) => nodes.filter((node) => node.context === 'project')));
  readonly otherNodes$ = this.menu.main$.pipe(map((nodes) => nodes.filter((node) => node.context !== 'project')));
  readonly byId$ = this.menu.main$.pipe(map((nodes) => {
    const byId = new Map<string, ResolvedNavNode>();
    const walk = (list: ResolvedNavNode[]) => list.forEach((node) => {
      byId.set(node.id, node);
      walk(node.children);
    });
    walk(nodes);
    return byId;
  }));

  searching = false;
  flyout: Flyout | null = null;
  tooltip: Tooltip | null = null;
  menuFor: string | null = null;
  renaming: string | null = null;
  /** Groups the person folded although the page lives inside them - for this page only. */
  private suppressed = new Set<string>();
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroyed = new Subject<void>();

  constructor(
    private menu: NavigationMenuService,
    private navigationState: NavigationStateService,
    private navLayout: NavLayoutService,
    private projectContext: ProjectContextService,
    private themeService: ThemeService,
    private announcer: LiveAnnouncer,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    router: Router,
  ) {
    router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroyed))
      .subscribe(() => {
        this.suppressed.clear();
        this.closeFlyout();
        this.menuFor = null;
        this.cdr.markForCheck();
        setTimeout(() => this.revealActive());
      });
    combineLatest([this.layout$, this.menu.main$]).pipe(takeUntil(this.destroyed))
      .subscribe(() => setTimeout(() => this.revealActive()));
    // A drawer or overlay that opens takes the focus, so Tab and Esc work inside it at once.
    let wasOpen = false;
    this.layout$.pipe(takeUntil(this.destroyed)).subscribe((state) => {
      const open = this.isDialog(state) || state.presentation === 'overlay';
      if (open && !wasOpen) {
        setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('.sn-sidebar__close, .sn-sidebar a, .sn-sidebar button')?.focus());
      }
      wasOpen = open;
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.clearTimers();
  }

  trackById(_index: number, node: {id?: string; key?: string}): string {
    return node.id || node.key;
  }

  isRail(state: NavLayoutState): boolean {
    return state.presentation === 'rail';
  }

  isDialog(state: NavLayoutState): boolean {
    return state.presentation === 'drawer' && state.drawerOpen;
  }

  isOpen(node: ResolvedNavNode, expanded: Set<string>, active: Set<string>): boolean {
    return expanded.has(node.id) || (active.has(node.id) && !this.suppressed.has(node.id));
  }

  toggleGroup(node: ResolvedNavNode, expanded: Set<string>, active: Set<string>): void {
    const open = this.isOpen(node, expanded, active);
    if (open) {
      if (expanded.has(node.id)) this.navigationState.setGroupExpanded(node.id, false);
      if (active.has(node.id)) this.suppressed.add(node.id);
    } else {
      this.suppressed.delete(node.id);
      this.navigationState.setGroupExpanded(node.id, true);
    }
  }

  isStarred(node: ResolvedNavNode, keys: Set<string>): boolean {
    const key = this.menu.starKey(node);
    return !!key && keys.has(key);
  }

  toggleStar(node: ResolvedNavNode, keys: Set<string>, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const was = this.isStarred(node, keys);
    const result = this.menu.toggleStar(node);
    if (result === 'added' || result === 'removed') {
      this.announcer.announce(`${was ? 'Removed' : 'Added'} ${node.label} ${was ? 'from' : 'to'} favorites`);
    }
  }

  // ── favorites ────────────────────────────────────────────────────────────

  toggleFavoriteMenu(favorite: ResolvedBookmark, event: Event): void {
    event.stopPropagation();
    this.menuFor = this.menuFor === favorite.key ? null : favorite.key;
  }

  moveFavorite(favorite: ResolvedBookmark, delta: -1 | 1): void {
    this.navigationState.moveBookmark(favorite.key, delta);
    this.announcer.announce(`Moved ${favorite.label} ${delta < 0 ? 'up' : 'down'}`);
  }

  startRename(favorite: ResolvedBookmark): void {
    this.menuFor = null;
    this.renaming = favorite.key;
    setTimeout(() => this.host.nativeElement.querySelector<HTMLInputElement>('.sn-favorite__rename')?.select());
  }

  finishRename(favorite: ResolvedBookmark, label: string): void {
    if (this.renaming !== favorite.key) return;
    this.renaming = null;
    if (label.trim() !== favorite.label) this.navigationState.renameBookmark(favorite.key, label);
  }

  cancelRename(): void {
    this.renaming = null;
  }

  removeFavorite(favorite: ResolvedBookmark): void {
    this.menuFor = null;
    this.navigationState.removeBookmark(favorite.key);
    this.announcer.announce(`Removed ${favorite.label} from favorites`);
  }

  // ── layout ───────────────────────────────────────────────────────────────

  toggleCollapsed(): void {
    this.navLayout.toggleCollapsed();
  }

  close(state: NavLayoutState): void {
    if (state.presentation === 'overlay') this.navLayout.closeOverlay();
    else this.navLayout.closeDrawer();
    setTimeout(() => document.querySelector<HTMLElement>('[data-sn-hamburger]')?.focus());
  }

  reload(): void {
    window.location.reload();
  }

  openPalette(): void {
    this.navLayout.openPalette();
  }

  afterPick(): void {
    this.searching = false;
    this.navLayout.closeDrawer();
    this.navLayout.closeOverlay();
    setTimeout(() => document.getElementById('sn-main')?.focus());
  }

  onQueryChange(query: string): void {
    this.searching = !!query.trim();
  }

  onNavigate(): void {
    this.closeFlyout();
  }

  // ── rail: tooltips and flyouts ───────────────────────────────────────────

  showTooltip(text: string, event: Event): void {
    if (this.flyout) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.tooltip = {text, top: rect.top + rect.height / 2};
  }

  hideTooltip(): void {
    this.tooltip = null;
  }

  flyoutEnter(id: string, label: string, event: Event): void {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (this.flyout?.id === id || this.flyout?.pinned) return;
    const trigger = event.currentTarget as HTMLElement;
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openFlyout(id, label, trigger, false);
      this.cdr.markForCheck();
    }, this.flyout ? 0 : OPEN_DELAY);
  }

  flyoutLeave(): void {
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = null;
    if (!this.flyout || this.flyout.pinned) return;
    this.closeTimer = setTimeout(() => {
      this.flyout = null;
      this.cdr.markForCheck();
    }, CLOSE_DELAY);
  }

  keepFlyout(): void {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  /** A click pins a flyout open, or closes the one it pinned. */
  flyoutClick(id: string, label: string, event: Event): void {
    this.clearTimers();
    if (this.flyout?.id === id && this.flyout.pinned) {
      this.closeFlyout();
      return;
    }
    this.openFlyout(id, label, event.currentTarget as HTMLElement, true);
  }

  flyoutKeydown(id: string, label: string, event: KeyboardEvent): void {
    if (['ArrowRight', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.openFlyout(id, label, event.currentTarget as HTMLElement, true);
      setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('.sn-flyout a, .sn-flyout button')?.focus());
    }
  }

  flyoutItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeFlyout(true);
    }
  }

  closeFlyout(returnFocus = false): void {
    const id = this.flyout?.id;
    this.clearTimers();
    this.flyout = null;
    if (returnFocus && id) {
      this.host.nativeElement.querySelector<HTMLElement>(`[data-sn-flyout-trigger="${id}"]`)?.focus();
    }
  }

  /** Esc wherever the focus is: a tooltip, then a flyout, then a favorite's menu, then the drawer. */
  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    const state = this.navLayout.state;
    if (state) this.onEscape(state);
  }

  onEscape(state: NavLayoutState): void {
    if (this.tooltip) {
      this.tooltip = null;
      return;
    }
    if (this.flyout) {
      this.closeFlyout(true);
      return;
    }
    if (this.menuFor) {
      this.menuFor = null;
      return;
    }
    if (this.isDialog(state) || state.presentation === 'overlay') this.close(state);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const inside = this.host.nativeElement.contains(event.target as Node);
    if (!inside && (this.flyout || this.menuFor)) {
      this.closeFlyout();
      this.menuFor = null;
      this.cdr.markForCheck();
    }
  }

  private openFlyout(id: string, label: string, trigger: HTMLElement, pinned: boolean): void {
    const rect = trigger.getBoundingClientRect();
    const minTop = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sn-env-bar-height')) || 0;
    // Anchored to the bottom when the trigger sits low, so the flyout never runs off screen.
    const top = window.innerHeight - rect.top < 240 ? Math.max(minTop + 8, rect.bottom - 240) : Math.max(minTop + 8, rect.top);
    this.tooltip = null;
    this.flyout = {id, label, top, pinned};
  }

  private clearTimers(): void {
    if (this.openTimer) clearTimeout(this.openTimer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.openTimer = null;
    this.closeTimer = null;
  }

  /**
   * Keep the active entry in view in a long menu - scrolling the menu only, and only when the
   * entry is out of sight, so the favorites above it stay where the person left them.
   */
  private revealActive(): void {
    const container = this.host.nativeElement.querySelector<HTMLElement>('.sn-sidebar__scroll');
    const active = container?.querySelector<HTMLElement>('.sn-nav-section--tree .sn-nav-link--active');
    if (!container || !active) return;
    const box = container.getBoundingClientRect();
    const item = active.getBoundingClientRect();
    if (item.top < box.top) container.scrollTop -= box.top - item.top + 8;
    else if (item.bottom > box.bottom) container.scrollTop += item.bottom - box.bottom + 8;
  }
}
